import React from "react";
import { Button } from "./ui/button";
import { Ruler, Camera, Check, X, Loader2, NotebookPen } from "lucide-react";

/** Live table of the voice-guided measurement session (shown inside Buddy's balloon). */
export function BuddyMeasureCard({ session, t, busy, onFinish, onCancel, onPhoto, onOpenNotes, onDismiss }) {
  if (!session) return null;
  const units = session.units || [];
  const exp = session.expected_count;
  const done = session.status === "done";
  const title = [session.customer_name, session.job_number ? `#${session.job_number}` : ""].filter(Boolean).join(" · ");
  return (
    <div data-testid="buddy-measure-card" className="rounded-xl border-2 border-sky-400/60 bg-sky-50 dark:bg-sky-950/30 px-3 py-2 space-y-1.5">
      <div className="flex items-center gap-1.5">
        <Ruler className="h-3.5 w-3.5 text-sky-700 dark:text-sky-300 shrink-0" />
        <span className="text-[11px] font-semibold text-sky-700 dark:text-sky-300 uppercase tracking-wide truncate flex-1" data-testid="buddy-measure-title">
          {done ? t("buddy_measure_saved") : t("buddy_measure_title")}{title ? ` · ${title}` : ""}
        </span>
        <span className="text-[11px] tabular-nums text-muted-foreground shrink-0" data-testid="buddy-measure-count">
          {units.length}{exp ? ` / ${exp}` : ""}
        </span>
      </div>
      {units.length === 0 && <div className="text-[12px] text-muted-foreground">{t("buddy_measure_empty")}</div>}
      {units.map((u) => (
        <div key={u.n} className="flex items-start gap-1.5 text-[12px]" data-testid="buddy-measure-unit">
          <span className="font-semibold tabular-nums shrink-0">{u.n}.</span>
          <div className="min-w-0 flex-1">
            <div className="truncate">
              <span className="font-medium">{u.location || "—"}</span>
              {u.subtype ? <span className="text-muted-foreground"> · {u.subtype}</span> : <span className="text-muted-foreground"> · {t(u.kind === "door" ? "measure_door" : "measure_window")}</span>}
            </div>
            <div className="font-mono-plex font-semibold text-sky-700 dark:text-sky-300">{u.value || "?"}</div>
            {u.note && <div className="text-[11px] text-muted-foreground italic truncate">{u.note}</div>}
          </div>
          {!done && (
            <button type="button" data-testid={`buddy-measure-photo-${u.n}`} onClick={() => onPhoto(u.n)} disabled={busy}
                    className={`h-7 w-7 rounded-md flex items-center justify-center shrink-0 ${u.photo_media_ids?.length ? "text-emerald-600 bg-emerald-500/10" : "text-muted-foreground hover:bg-black/5"}`}
                    aria-label="photo">
              <Camera className="h-3.5 w-3.5" />
            </button>
          )}
          {done && u.photo_media_ids?.length > 0 && <Camera className="h-3.5 w-3.5 text-emerald-600 shrink-0 mt-1" />}
        </div>
      ))}
      <div className="flex gap-1.5 pt-0.5">
        {done ? (
          <>
            <Button data-testid="buddy-measure-dismiss" size="sm" variant="outline" className="h-8 text-xs flex-1" onClick={onDismiss}>
              <X className="h-3.5 w-3.5 mr-1" />{t("close")}
            </Button>
            <Button data-testid="buddy-measure-open-notes" size="sm" className="h-8 text-xs flex-1" onClick={onOpenNotes}>
              <NotebookPen className="h-3.5 w-3.5 mr-1" />{t("buddy_measure_open_notes")}
            </Button>
          </>
        ) : (
          <>
            <Button data-testid="buddy-measure-cancel" size="sm" variant="outline" className="h-8 text-xs flex-1" disabled={busy} onClick={onCancel}>
              <X className="h-3.5 w-3.5 mr-1" />{t("buddy_cancel")}
            </Button>
            <Button data-testid="buddy-measure-finish" size="sm" className="h-8 text-xs flex-1" disabled={busy || units.length === 0} onClick={onFinish}>
              {busy ? <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" /> : <Check className="h-3.5 w-3.5 mr-1" />}{t("buddy_measure_finish")}
            </Button>
          </>
        )}
      </div>
    </div>
  );
}
